import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Container, 
  Alert, 
  Button, 
  Form, 
  Spinner, 
  Card,
  Badge,
  ListGroup,
  Row,
  Col
} from 'react-bootstrap';
import { db1 } from '../firebase/config';
import { doc, updateDoc, arrayUnion, serverTimestamp, onSnapshot } from 'firebase/firestore';

const SupportReplyPage = () => {
  const { token } = useParams();
  const [inquiry, setInquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [isArabic, setIsArabic] = useState(false);

  useEffect(() => {
    if (!token) return;

    const inquiryRef = doc(db1, 'support_inquiries', token);
    
    const unsubscribe = onSnapshot(inquiryRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setInquiry({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || null,
          updatedAt: data.updatedAt?.toDate() || null,
          messages: {
            ...data.messages,
            conversation: data.messages?.conversation?.map(msg => ({
              ...msg,
              timestamp: msg.timestamp?.toDate() || new Date()
            })) || []
          }
        });
        setLoading(false);
        // Set language based on inquiry
        if (data.language === 'ar') {
          setIsArabic(true);
          document.dir = 'rtl';
        }
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [token]);

  const handleSendReply = async (e) => {
    e.preventDefault();
    setSending(true);

    if (!newMessage.trim()) {
      setNotification({ show: true, type: 'danger', message: 'Message cannot be empty' });
      setSending(false);
      return;
    }

    try {
      const messageData = {
        sender: 'support',
        message: newMessage.trim(),
        timestamp: new Date(),
        supportAgent: 'Support Team'
      };

      await updateDoc(doc(db1, 'support_inquiries', token), {
        'messages.conversation': arrayUnion(messageData),
        'messages.lastUpdated': serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'replied'
      });

      setNewMessage('');
      setNotification({ show: true, type: 'success', message: 'Reply sent successfully!' });

    } catch (err) {
      setNotification({ show: true, type: 'danger', message: `Failed to send reply: ${err.message}` });
    } finally {
      setSending(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 50%, #ffffff 100%)',
      padding: '2rem 0',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      direction: isArabic ? 'rtl' : 'ltr'
    },
    content: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 1rem'
    },
    headerCard: {
      backgroundColor: 'white',
      border: '2px solid rgba(220, 53, 69, 0.1)',
      borderRadius: '15px',
      boxShadow: '0 10px 30px rgba(220, 53, 69, 0.08)',
      marginBottom: '2rem'
    },
    chatContainer: {
      border: '2px solid rgba(220, 53, 69, 0.1)',
      borderRadius: '15px',
      overflow: 'hidden',
      backgroundColor: '#ffffff',
      boxShadow: '0 10px 30px rgba(220, 53, 69, 0.08)'
    },
    messagesArea: {
      height: '500px',
      overflowY: 'auto',
      padding: '1.5rem',
      backgroundColor: '#fff5f5'
    },
    messageBubble: {
      padding: '1rem 1.5rem',
      borderRadius: '15px',
      marginBottom: '1rem',
      maxWidth: '80%',
      wordWrap: 'break-word'
    },
    inputArea: {
      padding: '1.5rem',
      borderTop: '2px solid rgba(220, 53, 69, 0.1)',
      backgroundColor: '#ffffff'
    },
    formControl: {
      backgroundColor: '#ffffff',
      border: '2px solid rgba(220, 53, 69, 0.1)',
      borderRadius: '10px',
      padding: '12px 15px',
      fontSize: '1rem',
      resize: 'none'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <Container className="text-center my-5 py-5">
          <Spinner animation="border" style={{ color: '#dc3545' }} />
          <p className="mt-3" style={{ color: '#dc3545' }}>Loading inquiry...</p>
        </Container>
      </div>
    );
  }

  if (!inquiry) {
    return (
      <div style={styles.container}>
        <Container className="my-5">
          <Alert variant="danger" style={{ borderRadius: '10px' }}>
            Inquiry not found
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        {notification.show && (
          <Alert 
            variant={notification.type} 
            style={{ borderRadius: '10px', marginBottom: '2rem' }}
            onClose={() => setNotification({ show: false })} 
            dismissible
          >
            {notification.message}
          </Alert>
        )}

        {/* Inquiry Header */}
        <Card style={styles.headerCard}>
          <Card.Header style={{ 
            backgroundColor: '#dc3545', 
            color: 'white',
            borderBottom: '2px solid rgba(220, 53, 69, 0.1)',
            padding: '1.5rem'
          }}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4 style={{ margin: 0, fontWeight: '600' }}>🛠️ Support Inquiry</h4>
                <p style={{ margin: '0.5rem 0 0 0', opacity: '0.9' }}>Token: {token}</p>
              </div>
              <Badge 
                bg={inquiry.status === 'new' ? 'warning' : 
                    inquiry.status === 'waiting_support' ? 'info' : 'success'}
                style={{ 
                  fontSize: '0.9rem',
                  padding: '0.5rem 1rem',
                  borderRadius: '20px',
                  fontWeight: '600'
                }}
              >
                {inquiry.status}
              </Badge>
            </div>
          </Card.Header>
          <Card.Body style={{ padding: '1.5rem' }}>
            <Row>
              <Col md={6}>
                <p><strong>📅 Created:</strong> {inquiry.createdAt?.toLocaleString() || 'N/A'}</p>
                <p><strong>🔄 Last Updated:</strong> {inquiry.updatedAt?.toLocaleString() || 'N/A'}</p>
              </Col>
              <Col md={6}>
                <p><strong>🌐 Language:</strong> {inquiry.language || 'N/A'}</p>
                <p><strong>💬 Messages:</strong> {inquiry.messages.conversation.length}</p>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Chat Interface */}
        <div style={styles.chatContainer}>
          <div style={{ 
            backgroundColor: '#dc3545', 
            color: 'white',
            padding: '1.5rem',
            borderBottom: '2px solid rgba(220, 53, 69, 0.1)'
          }}>
            <h5 style={{ margin: 0, fontWeight: '600' }}>💬 Live Conversation</h5>
          </div>
          
          {/* Messages Area */}
          <div style={styles.messagesArea}>
            {inquiry.messages.conversation.length > 0 ? (
              inquiry.messages.conversation.map((msg, index) => (
                <div
                  key={index}
                  style={{
                    ...styles.messageBubble,
                    backgroundColor: msg.sender === 'support' ? '#d4edda' : '#e9ecef',
                    color: '#1a1a1a',
                    marginLeft: msg.sender === 'support' ? 'auto' : '0',
                    marginRight: msg.sender === 'support' ? '0' : 'auto',
                    border: '1px solid rgba(220, 53, 69, 0.1)'
                  }}
                >
                  <div style={{ fontWeight: '600', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                    {msg.sender === 'support' ? '🛠️ Support Team' : '👤 Client'}
                  </div>
                  <div style={{ lineHeight: '1.5' }}>{msg.message}</div>
                  <div style={{ 
                    fontSize: '0.75rem', 
                    opacity: '0.7',
                    marginTop: '0.5rem',
                    textAlign: msg.sender === 'support' ? 'right' : 'left'
                  }}>
                    {msg.timestamp.toLocaleString()}
                  </div>
                </div>
              ))
            ) : (
              <div style={{ 
                textAlign: 'center', 
                color: '#666',
                padding: '3rem 1rem',
                fontStyle: 'italic'
              }}>
                No messages yet
              </div>
            )}
          </div>

          {/* Input Area */}
          <div style={styles.inputArea}>
            <Form onSubmit={handleSendReply}>
              <Form.Group>
                <Form.Label style={{ fontWeight: '600', color: '#1a1a1a', marginBottom: '0.5rem' }}>
                  ✏️ Your Reply
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="Type your professional reply here..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  style={styles.formControl}
                />
              </Form.Group>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <Button
                  type="submit"
                  disabled={sending || !newMessage.trim()}
                  style={{
                    backgroundColor: '#dc3545',
                    borderColor: '#dc3545',
                    borderRadius: '10px',
                    padding: '12px 30px',
                    fontWeight: '600',
                    fontSize: '1rem'
                  }}
                >
                  {sending ? (
                    <>
                      <Spinner animation="border" size="sm" style={{ marginRight: '0.5rem' }} />
                      Sending...
                    </>
                  ) : (
                    'Send Reply'
                  )}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportReplyPage;
